import { SuperpowersChecklist } from "#webapp/src/components/superpowers-checklist/SuperpowersChecklist";
import { useLazyQuery } from "@apollo/client";
import classNames from "classnames";
import { DateTime } from "luxon";
import React from "react";
import { CurrentOrNextEventProvider } from "../../../../hooks/useCurrentOrNextEvent/useCurrentOrNextEvent";
import { useFeatureFlag } from "../../../../launch-darkly/useLaunchDarkly";
import { track, TrackingEvents } from "../../../../util/analytics.util";
import { OnDemandDefragDocument } from "../../../chat-plus-calendar/apollo/__generated__/OnDemandDefrag.generated";
import { useUpdateDefragProposal } from "../../../chat-plus-calendar/DefragProposalContext";
import { eventToast } from "../../../web-app-calendar/notifiation-event/EventToast";
import { optimizeOptionIcon } from "../ChatInput/Icons";
import { useAIMessageContext } from "../hooks/AIMessageContext";
import { useCollaborators } from "../hooks/useCollaborators";
import { AINextMeetingLink } from "./AINextMeetingLink";
import { CustomCollaborators } from "./CustomCollaborators";
import { TryClockwiseChecklist } from "./TryClockwiseChecklist";

const OptimizeWeekButton = () => {
  const [getOnDemandDefrag] = useLazyQuery(OnDemandDefragDocument);
  const { handleOnDemandDefragRequest } = useUpdateDefragProposal();

  const { setProcessingMessage } = useAIMessageContext();

  const onClick = async () => {
    setProcessingMessage(true);
    track(TrackingEvents.CHAT.ODD.ODD_REQUEST_CLICKED);
    const response = await getOnDemandDefrag({
      variables: {
        startTime: DateTime.now(),
      },
    });
    const defragProposal = response.data?.viewer?.user?.onDemandDefrag || null;
    track(TrackingEvents.CHAT.ODD.ODD_SUGGESTIONS_RECEIVED, { proposalId: defragProposal?.id });
    setProcessingMessage(false);
    if (defragProposal?.diffBlocks?.[0]?.diffs?.length) {
      handleOnDemandDefragRequest(defragProposal);
    } else {
      eventToast.error({
        operation: "DEFRAG",
        title: "Nothing available to move",
      });
    }
    return;
  };

  return (
    <button
      onClick={onClick}
      className={classNames(
        "cw-text-neutral cw-border-muted hover:cw-bg-neutral-hover active:cw-bg-neutral-hover cw-bg-default",
        "cw-h-8 cw-text-12 cw-px-2 cw-font-medium",
        "cw-rounded-md cw-w-full",
        "focus-visible:cw-outline-2 focus-visible:cw-outline-offset-2",
        "cw-border cw-border-solid cw-transition-colors cw-ease-out cw-duration-200",
        "cw-leading-none cw-font-body cw-whitespace-nowrap",
        "cw-flex cw-items-center cw-justify-between cw-cursor-pointer",
      )}
    >
      <div className="cw-flex cw-items-center cw-gap-1.5">
        {optimizeOptionIcon}
        Optimize my week
      </div>
      <div className="cw-font-semibold cw-text-subtle cw-text-[9px]">ALPHA</div>
    </button>
  );
};

export const SidebarQuickFeed = () => {
  const [onDemandDefragAvailable] = useFeatureFlag("onDemandDefrag");
  const [checklistAvailable] = useFeatureFlag("NUX-Q1-2025");
  const { loadingHistory } = useAIMessageContext();
  const {
    topCollaborators,
    customCollaborators,
    loading: collaboratorsLoading,
  } = useCollaborators();

  if (loadingHistory) return null;

  return (
    <CurrentOrNextEventProvider>
      <div className="cw-flex cw-flex-col cw-gap-5 cw-w-full cw-h-full">
        {onDemandDefragAvailable && <OptimizeWeekButton />}
        <AINextMeetingLink />
        {checklistAvailable && <SuperpowersChecklist />}
        <CustomCollaborators
          loading={collaboratorsLoading}
          topCollaborators={topCollaborators}
          customCollaborators={customCollaborators}
        />
        <TryClockwiseChecklist />
      </div>
    </CurrentOrNextEventProvider>
  );
};
